import type { LocalPersistContext } from '@src/modules/shared/local-sqlite/domain/LocalPersistContext.ts';
import {
  flushLocalPersistAfterMutation,
  rejectIfStorageQuotaBlocksSave,
} from '@src/modules/shared/local-sqlite/application/localPersistAfterMutation.ts';
import {
  collectPuntoEntregaMediaDataUrls,
} from '@src/modules/sync/domain/estimateMediaPayloadBytes.ts';
import type { PuntosEntregaRepositoryPort } from '../domain/ports/PuntosEntregaRepositoryPort.ts';
import type { PuntoEntregaFormSnapshot } from '../domain/puntoEntregaFormSnapshot.ts';
import {
  sliceCatalogAfterComunaChange,
  type ComunaChangeSlice,
} from './puntosEntregaCatalogSlices.ts';
import { buildPuntoEntregaDetailFromFormSnapshot } from './puntosEntregaFormDetail.ts';
import {
  buildCreateFormOpenPatch as buildCreateFormOpenPatchState,
  buildEditFormOpenPatch as buildEditFormOpenPatchState,
  type PuntosEntregaFormOpenPatch,
} from './puntosEntregaFormOpenState.ts';
import {
  findParticipanteBusquedaForPeForm,
  findParticipanteBusquedaForPeFormByParticipanteId,
  type ParticipanteBusquedaPeFormResult,
} from './puntosEntregaParticipantBusqueda.ts';
import {
  joinDateTime,
  puntoEntregaLatLonValidos,
  resolveEntregaIdForSelect,
} from '../domain/puntoEntregaFormUtils.ts';
import type {
  CatalogRow,
  ParticipantePuntosEntregaBusquedaPrefill,
  ParticipanteResumen,
  PuntoEntregaDetail,
  PuntoEntregaUploadedCleanupSummary,
  PuntosEntregaListPage,
  PuntosEntregaListQuery,
  SavePuntoEntregaPayload,
} from '../domain/puntoEntregaModel.ts';
import type { ParticipanteBusquedaCandidate } from '../../shared/participante/domain/participanteBusquedaCandidate.ts';
import { joinMissingFieldLabelsEs } from '@core/domain/utils/joinMissingFieldLabelsEs.ts';
import { ok, fail, type Result } from '@core/domain/utils/Result.ts';
import {
  legacyLugarExistsInComuna,
  type PuntoEntregaLugarOrigen,
} from '../domain/puntoEntregaLugarOrigen.ts';

const WARN_NO_ENTREGAS = 'No hay tipos de entrega en el catálogo local.';

function pushCatalogoUbicacionIssues(
  repo: PuntosEntregaRepositoryPort,
  s: Pick<PuntoEntregaFormSnapshot, 'comunaId' | 'lugarId' | 'lugarOrigen'>,
  issues: string[],
): void {
  const comunaOk = s.comunaId > 0 && repo.listComunas().some((c) => c.id === s.comunaId);
  if (!comunaOk) issues.push('Comuna');
  let lugarOk = false;
  if (comunaOk && s.lugarId > 0 && s.lugarOrigen) {
    if (s.lugarOrigen === 'catalogo') {
      const cat = repo.findPuntosEntregaCatalogoById(s.lugarId);
      lugarOk = cat != null && cat.comunaId === s.comunaId;
    } else {
      lugarOk = legacyLugarExistsInComuna(repo, s.comunaId, s.lugarId);
    }
  }
  if (comunaOk && !lugarOk) issues.push('Lugar');
}

function collectPuntoEntregaFormIssues(
  repo: PuntosEntregaRepositoryPort,
  s: PuntoEntregaFormSnapshot,
  opts?: { requireParticipants?: boolean },
): string[] {
  const issues: string[] = [];
  const entregaOpts = repo.listEntregas();
  if (entregaOpts.length === 0) {
    return [WARN_NO_ENTREGAS];
  }

  pushCatalogoUbicacionIssues(repo, s, issues);

  if (s.esRemanente !== 'si' && s.esRemanente !== 'no') {
    issues.push('¿Es remanente?');
  }
  if (!s.fecha.trim()) issues.push('Fecha');
  if (!s.hora.trim()) issues.push('Hora');
  if (!s.responsableNombre && (!s.responsableId || s.responsableId <= 0)) {
    issues.push('Responsable (FO-GESR)');
  }
  if (!puntoEntregaLatLonValidos(s.latitud, s.longitud)) {
    issues.push('Ubicación (coordenadas GPS)');
  }
  if (opts?.requireParticipants !== false && !s.participantesDraft?.length) {
    issues.push('Al menos un participante');
  }

  const eid = resolveEntregaIdForSelect(entregaOpts, s.entregaId);
  if (!entregaOpts.find((e) => e.id === eid)) {
    issues.push('Número de entrega');
  }

  return issues;
}

function buildSavePuntoEntregaPayload(
  repo: PuntosEntregaRepositoryPort,
  s: PuntoEntregaFormSnapshot,
  entregaIdForUi: number,
): SavePuntoEntregaPayload {
  const lugarOrigen = s.lugarOrigen as PuntoEntregaLugarOrigen;
  const catalog = lugarOrigen === 'catalogo' ? repo.findPuntosEntregaCatalogoById(s.lugarId) : null;
  const direccion = catalog?.direccion.trim() || s.direccion.trim();
  return {
    id: s.editingId ?? undefined,
    entregaId: entregaIdForUi,
    esRemanente: s.esRemanente === 'si',
    lugarId: s.lugarId,
    lugarOrigen,
    fechaHora: joinDateTime(s.fecha, s.hora),
    latitud: s.latitud,
    longitud: s.longitud,
    direccion,
    responsableId: s.responsableId,
    informePdf: s.informePdf,
    participantes: s.participantesDraft,
  };
}

export type SavePuntoEntregaFromFormResult =
  | { ok: false; message: string }
  | { ok: true; id: number; entregaIdForUi: number };

/** Delivery-point use cases over {@link PuntosEntregaRepositoryPort}. */
export class PuntosEntregaApplicationService {
  constructor(
    private readonly repo: PuntosEntregaRepositoryPort,
    private readonly localPersist?: LocalPersistContext,
  ) {}

  listPuntosEntrega(q: PuntosEntregaListQuery): PuntosEntregaListPage {
    return this.repo.listPuntosEntrega(q);
  }

  getPuntoEntrega(id: number): PuntoEntregaDetail | null {
    return this.repo.getPuntoEntrega(id);
  }

  savePuntoEntrega(payload: SavePuntoEntregaPayload): number {
    return this.repo.savePuntoEntrega(payload);
  }

  /** Validates form snapshot and persists; returns validation message without throwing. */
  findParticipanteBusquedaForForm(
    docBusqueda: string,
    puntoEntregaComunaId: number,
  ): ParticipanteBusquedaPeFormResult {
    return findParticipanteBusquedaForPeForm(this.repo, docBusqueda, puntoEntregaComunaId);
  }

  findParticipanteBusquedaForFormByParticipanteId(
    participanteId: number,
    puntoEntregaComunaId: number,
  ): ParticipanteBusquedaPeFormResult {
    return findParticipanteBusquedaForPeFormByParticipanteId(this.repo, participanteId, puntoEntregaComunaId);
  }

  listParticipanteBusquedaCandidates(docBusqueda: string): ParticipanteBusquedaCandidate[] {
    const t = docBusqueda.trim();
    if (!t) return [];
    return this.repo.listParticipanteBusquedaCandidates(t);
  }

  /** Header fields required before opening the participant modal (excludes participant list). */
  collectHeaderIssuesForParticipanteModal(s: PuntoEntregaFormSnapshot): string[] {
    return collectPuntoEntregaFormIssues(this.repo, s, { requireParticipants: false });
  }

  sliceCatalogAfterComunaChange(comunaId: number): ComunaChangeSlice {
    return sliceCatalogAfterComunaChange(this.repo, comunaId);
  }

  buildDetailFromFormSnapshot(f: PuntoEntregaFormSnapshot): PuntoEntregaDetail | null {
    return buildPuntoEntregaDetailFromFormSnapshot(this.repo, f);
  }

  buildCreateFormOpenPatch(now: Date = new Date()): PuntosEntregaFormOpenPatch {
    return buildCreateFormOpenPatchState(this.repo, now);
  }

  buildEditFormOpenPatch(editingId: number, d: PuntoEntregaDetail): PuntosEntregaFormOpenPatch {
    return buildEditFormOpenPatchState(this.repo, editingId, d);
  }

  async savePuntoEntregaFromForm(s: PuntoEntregaFormSnapshot): Promise<SavePuntoEntregaFromFormResult> {
    const issues = collectPuntoEntregaFormIssues(this.repo, s);
    if (issues.length > 0) {
      return { ok: false, message: joinMissingFieldLabelsEs(issues) };
    }

    const entregaOpts = this.repo.listEntregas();
    const entregaIdForUi = resolveEntregaIdForSelect(entregaOpts, s.entregaId);
    if (!entregaOpts.find((e) => e.id === entregaIdForUi)) {
      return { ok: false, message: joinMissingFieldLabelsEs(['Número de entrega']) };
    }

    const payload = buildSavePuntoEntregaPayload(this.repo, s, entregaIdForUi);
    const quotaBlock = await rejectIfStorageQuotaBlocksSave(this.localPersist, collectPuntoEntregaMediaDataUrls(payload));
    if (quotaBlock) return { ok: false, message: quotaBlock };

    const id = this.repo.savePuntoEntrega(payload);
    await flushLocalPersistAfterMutation(this.localPersist);
    return { ok: true, id, entregaIdForUi };
  }

  listEntregas(): CatalogRow[] {
    return this.repo.listEntregas();
  }

  listComunas(): CatalogRow[] {
    return this.repo.listComunas();
  }

  listBarriosByComuna(comunaId: number): CatalogRow[] {
    return this.repo.listBarriosByComuna(comunaId);
  }

  listLugaresByBarrio(barrioId: number): CatalogRow[] {
    return this.repo.listLugaresByBarrio(barrioId);
  }

  listLugaresByComuna(comunaId: number): CatalogRow[] {
    return this.repo.listLugaresByComuna(comunaId);
  }

  findPuntosEntregaCatalogoById(id: number) {
    return this.repo.findPuntosEntregaCatalogoById(id);
  }

  listTiposDocumento(): CatalogRow[] {
    return this.repo.listTiposDocumento();
  }

  listTiposBonos(): CatalogRow[] {
    return this.repo.listTiposBonos();
  }

  listParentesco(): CatalogRow[] {
    return this.repo.listParentesco();
  }

  findParticipanteByDocumento(documento: string): ParticipanteResumen | null {
    const trimmed = documento.trim();
    if (!trimmed) return null;
    return this.repo.findParticipanteByDocumento(trimmed);
  }

  findParticipanteById(id: number): ParticipanteResumen | null {
    return this.repo.findParticipanteById(id);
  }

  findParticipantePuntosEntregaBusquedaPrefillPorDocumento(
    documento: string,
  ): ParticipantePuntosEntregaBusquedaPrefill | null {
    const trimmed = documento.trim();
    if (!trimmed) return null;
    return this.repo.findParticipantePuntosEntregaBusquedaPrefillPorDocumento(trimmed);
  }

  findPuntoEntregaIdForParticipante(participanteId: number): number | null {
    return this.repo.findPuntoEntregaIdForParticipante(participanteId);
  }

  findConflictingPuntoEntregaIdForParticipante(
    participanteId: number,
    entregaCatalogId: number,
    excludedPuntoEntregaId: number | null,
  ): number | null {
    return this.repo.findConflictingPuntoEntregaIdForParticipante(
      participanteId,
      entregaCatalogId,
      excludedPuntoEntregaId,
    );
  }

  resolveBonoIdForPuntoEntregaCaptura(caption: string, tipoBonoCatalogId: number): number | null {
    return this.repo.resolveBonoIdForPuntoEntregaCaptura(caption, tipoBonoCatalogId);
  }

  participanteTipoBonoCatalogId(participanteId: number): number | null {
    return this.repo.participanteTipoBonoCatalogId(participanteId);
  }

  getBonoNombreById(bonoId: number): string | null {
    return this.repo.getBonoNombreById(bonoId);
  }

  listUploadedOfflinePuntosEntrega(): PuntoEntregaUploadedCleanupSummary[] {
    return this.repo.listUploadedOfflinePuntosEntrega();
  }

  countPendingOfflineExport(): number {
    return this.repo.countPendingOfflineExport();
  }

  deleteUploadedPuntoEntrega(id: number): Result<void, string> {
    try {
      this.repo.deleteUploadedPuntoEntrega(id);
      return ok(undefined);
    } catch (e) {
      const m = e instanceof Error ? e.message : String(e);
      return fail(m || 'No se pudo eliminar el punto de entrega.');
    }
  }
}
